/**
 * Phase 41 Plan 02 — TanStack Query hooks for the cron-jobs page.
 *
 * D-05: refetchInterval 15_000 (15s whole-endpoint poll). No SSE, no per-row
 * polling, no manual refresh button. refetchIntervalInBackground: false stops
 * polling on window blur — matches usePendingTimesheets / useBoard precedent.
 *
 * D-06: useRunCronJob is instant-fire; onSuccess invalidates the ['cron-jobs']
 * query so the next refetch reflects the new audit row + manual sub-row.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCronJobs,
  runCronJob,
  updateCronJobOverride,
  resetCronJobOverride,
} from '../services/api/cronJobs';
import type {
  CronJobRow,
  RunCronJobResult,
  UpdateCronJobOverrideInput,
  CronJobOverrideResult,
} from '../services/api/cronJobs';

export function useCronJobs() {
  return useQuery<CronJobRow[]>({
    queryKey: ['cron-jobs'],
    queryFn: () => getCronJobs(),
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

export function useRunCronJob() {
  const queryClient = useQueryClient();
  return useMutation<RunCronJobResult, Error, string>({
    mutationFn: (schedulerId: string) => runCronJob(schedulerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cron-jobs'] });
    },
  });
}

/** Phase 56 (CRON-CFG-03) — PUT /api/admin/cron-jobs/:name override. */
export function useUpdateCronJobOverride(name: string) {
  const queryClient = useQueryClient();
  return useMutation<CronJobOverrideResult, Error, UpdateCronJobOverrideInput>({
    mutationFn: (input: UpdateCronJobOverrideInput) => updateCronJobOverride(name, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cron-jobs'] });
    },
  });
}

/** Phase 56 (CRON-CFG-04) — DELETE /api/admin/cron-jobs/:name (reset to default). */
export function useResetCronJobOverride(name: string) {
  const queryClient = useQueryClient();
  return useMutation<CronJobOverrideResult, Error, void>({
    mutationFn: () => resetCronJobOverride(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cron-jobs'] });
    },
  });
}
